import React from 'react';

import clsx from 'classnames';
import { Card as MuiCard, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import _ from 'lodash';
import {
  phoneFormatter,
  moneyFormat2Decimals,
} from '../../../../utils/appUtils';
import { withShowable } from '../../../_global/lib/withShowable';

const HiddenGrid = withShowable(Grid);

const P100AccountInformation = ({ data }: any) => {
  const classes = useStyles();

  return (
    <MuiCard style={{ paddingBottom: '2.08rem', marginBottom: '2rem' }}>
        <Grid container direction="column">
          <Grid item md={12}>
            <Grid container direction="row">
              <div className="flex--spaced">
                <h3 className={clsx('contrast-text', classes.miniheading)}>
                  Account Information
                </h3>
              </div>
            </Grid>
          </Grid>
          <Grid style={{ maxHeight: '18.6rem' }} item md={12}>
            <Grid container direction="row">
              <LeftColumn data={data} />
              <MiddleColumn data={data} />
              <RightColumn data={data} />
            </Grid>
          </Grid>
        </Grid>
      </MuiCard>
  );
};

const LeftColumn = ({ data }: any) => {
  const classes = useStyles();

  const additionalInsuredData = _.get(data, 'additionalNamedInsured', []);

  const StandarizeAdditionalInsured = React.useMemo(() => {
    if (!additionalInsuredData.length) {
      return 'None';
    }

    return additionalInsuredData.join(', ');
  }, [additionalInsuredData]);

  return (
    <Grid item md={4} style={{ paddingLeft: '2.083rem' }}>
      <Grid container direction="row" style={{ marginBottom: '0.625rem' }}>
        <Grid item md={2}>
          <span>Name:</span>
        </Grid>
        <Grid item md={2} />
        <Grid item md={7}>
          <span className={classes.textInfo}>{_.get(data, 'name', '-')}</span>
        </Grid>
      </Grid>
      <Grid container direction="row" style={{ marginBottom: '0.625rem' }}>
        <Grid item md={2}>
          <span>DBA:</span>
        </Grid>
        <Grid item md={2} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'dbaOrTradestyle', '-')}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={2}>
          <span>Address:</span>
        </Grid>
        <Grid item md={2} />
        <Grid item md={7} direction="column">
          <Grid container direction="row">
            <Grid item md={12}>
              <span className={classes.textInfo}>
                {_.get(data, 'address1', '-')}
              </span>
            </Grid>
            <Grid item md={12}>
              <span className={classes.textInfo}>
                {_.get(data, 'city', '-')}
              </span>
            </Grid>
            <Grid item md={12}>
              <span className={classes.textInfo}>
                {_.get(data, 'state', '-')}
                {', '} {_.get(data, 'zipCode', '-')}
              </span>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container direction="row" style={{ paddingBottom: '0.625rem' }}>
        <Grid item md={2}>
          <span>Phone:</span>
        </Grid>
        <Grid item md={2} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'phoneNumber', false)
              ? // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
                `${phoneFormatter(_.get(data, 'phoneNumber', '-'))}`
              : '-'}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={3}>
          <span>Additional Named Insured:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {StandarizeAdditionalInsured}
          </span>
        </Grid>
      </Grid>
    </Grid>
  );
};

const MiddleColumn = ({ data }: any) => {
  const classes = useStyles();

  const secondaryNaicsPercentage =
    100 - _.get(data, 'primaryNaicsPercentage', 0);

  const naicsDisplay = secondaryNaicsPercentage
    ? `${_.get(data, 'naicsCode', '-')} - ${_.get(
        data,
        'primaryNaicsPercentage',
        '-'
      )}%`
    : _.get(data, 'naicsCode', '-');

  const secondaryNaics = _.get(data, 'naicsCode', false)
    ? `${_.get(data, 'naicsCode', '-')} - ${secondaryNaicsPercentage}%`
    : '-';

  const projRevenue = _.get(data, 'revenue', false)
    ? moneyFormat2Decimals(_.get(data, 'revenue', '-'))
    : '-';

  return (
    <Grid item md={4} className={classes.border}>
      <Grid
        container
        direction="row"
        style={{
          marginBottom: secondaryNaicsPercentage ? '0rem' : '0.625rem',
        }}
      >
        <Grid item md={4}>
          <span>Primary Industry Code:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'naicsCode', false) ? naicsDisplay : '-'}
          </span>
        </Grid>
      </Grid>
      <HiddenGrid
        // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'boolean |... Remove this comment to see the full error message
        show={secondaryNaicsPercentage}
        container
        direction="row"
        style={{ marginBottom: secondaryNaicsPercentage ? '0.625rem' : '0rem' }}
      >
        <Grid item md={4}>
          <span>Secondary Industry Code:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>{secondaryNaics}</span>
        </Grid>
      </HiddenGrid>

      <Grid container direction="row">
        <Grid item md={4}>
          <span>Projected Revenue:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>{projRevenue}</span>
        </Grid>
      </Grid>

      <Grid container direction="row" style={{ marginBottom: '0.625rem' }}>
        <Grid item md={4}>
          <span>Number of Employees</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'noOfEmployeesAll', '-')}
          </span>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item md={4}>
          <span>Year Established:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'yearEstablished', '-')}
          </span>
        </Grid>
      </Grid>

      <Grid container direction="row" style={{ marginBottom: '0.625rem' }}>
        <Grid item md={4}>
          <span>Entity Type:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'entityType', '-')}
          </span>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item md={4}>
          <span>Ownership Type:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'ownershipType', '-')}
          </span>
        </Grid>
      </Grid>

      <Grid container direction="row" style={{ marginBottom: '0.625rem' }}>
        <Grid item md={4}>
          <span>Subsidiary/Franchisee:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'isFranchise', '-')}
          </span>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item md={4}>
          <span>DUNS Number:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={5}>
          <span className={classes.textInfo}>
            {_.get(data, 'dunsNumber', '-')}
          </span>
        </Grid>
      </Grid>
    </Grid>
  );
};

const RightColumn = ({ data }: any) => {
  const classes = useStyles();

  const phone = _.get(data, 'policyContactPhone', false)
    ? // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
      phoneFormatter(_.get(data, 'policyContactPhone'))
    : '-';

  const internationalSales = Number.isFinite(
    _.get(data, 'percentInternationalSales')
  )
    ? `${_.get(data, 'percentInternationalSales', 0)}%`
    : '-';

  return (
    <Grid item md={4} style={{ paddingLeft: '2.083rem' }}>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>URL:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>{_.get(data, 'url', '-')}</span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>Primary Web Domain:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'domainName', '-')}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>Secondary Web Domain(s):</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {data.allOtherDomains?.join?.(',') ?? ''}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row" style={{ paddingBottom: '0.625rem' }}>
        <Grid item md={4}>
          <span>Number of PII/PHI/PCI Records:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'numberOfPersonalRecords', '-')}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>% of International Sales:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>{internationalSales}</span>
        </Grid>
      </Grid>
      <Grid container direction="row" style={{ paddingBottom: '0.625rem' }}>
        <Grid item md={4}>
          <span>Current Contact:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'policyContactName', '-')}
          </span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>Current Contact Phone Number:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>{phone}</span>
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4}>
          <span>Current Contact Email:</span>
        </Grid>
        <Grid item md={1} />
        <Grid item md={7}>
          <span className={classes.textInfo}>
            {_.get(data, 'policyContactEmail', '-')}
          </span>
        </Grid>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(({ config, palette }) => ({
  border: {
    paddingLeft: '2.083rem',
    borderLeft: `solid ${palette.primary.contrastText} 0.0834rem`,
    borderRight: `solid ${palette.primary.contrastText} 0.0834rem`,
  },
  textInfo: {
    color: palette.primary.contrastText,
  },
  miniheading: {
    fontWeight: config.weights.bold,
    fontSize: config.textSizes.canon,
    paddingLeft: '2.083rem',
    marginBottom: '0.75rem',
    marginTop: '0.75rem',
  },
}));

export default P100AccountInformation;
